import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, Layers, DollarSign, Activity, RefreshCw, Mail, Zap, ShieldCheck, ArrowLeft, Clock, TrendingUp, SlidersHorizontal, Coins, X } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getMyUsage, getUserUsageAdmin, emailMyUsageReport, createCreditRequest, getMyCreditRequest } from '../api';
import { useTheme } from '../contexts/ThemeContext';
import { useOrg } from '../contexts/OrgContext';
import { TokenValue } from '../components/TokenValue';
import FilterDrawer from '../components/FilterDrawer';
import Select from '../components/Select';

const fmt  = (n) => (n || 0).toLocaleString();
const fmtM = (n) => ((n || 0) / 1e6).toFixed(2) + 'M';
// `dailyChart`/`dailyProv` collapse to month buckets ('YYYY-MM') once a
// range has too many days (see _collapse_daily in llm_stats.py); day/week
// keys stay 'YYYY-MM-DD'. Hardcoded month names, not toLocaleDateString —
// Intl's 'short' month for September is locale-dependent ("Sep" vs "Sept").
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDailyId = (id) => {
  if (!id) return id;
  if (/^\d{4}-\d{2}$/.test(id)) {
    const [y, m] = id.split('-');
    return `${MONTH_ABBR[Number(m) - 1]} ${y.slice(2)}`;
  }
  return id.slice(5);
};
// Explicit tick spacing instead of Recharts' width-based default, which skips
// a different number of labels per chart size. The +2 slack avoids jumping
// from "show all" straight to "every 2nd" when count is barely over target.
const tickInterval = (n, target = 7) => (n <= target + 2 ? 0 : Math.max(0, Math.ceil(n / target) - 1));

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PROVIDER_COLORS = ['#f97316', '#3b82f6', '#a855f7', '#22c55e', '#ec4899', '#eab308', '#14b8a6', '#ef4444'];

// Compact hour×day heatmap for My Usage.
function MiniHeatmap({ cells, maxRequests }) {
  const grid = {};
  (cells || []).forEach(c => { grid[`${c.dow}-${c.hour}`] = c; });
  const hours = Array.from({ length: 24 }, (_, h) => h);
  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="flex">
          <div className="w-8" />
          {hours.map(h => <div key={h} className="w-4 text-center text-[8px] text-fg-muted">{h % 6 === 0 ? h : ''}</div>)}
        </div>
        {DOW.map((label, d) => (
          <div key={d} className="flex items-center">
            <div className="w-8 text-[9px] text-fg-muted pr-1 text-right">{label}</div>
            {hours.map(h => {
              const n = grid[`${d}-${h}`]?.requests || 0;
              const intensity = maxRequests ? Math.min(1, n / maxRequests) : 0;
              const bg = n === 0 ? 'rgba(128,128,128,0.1)' : `rgba(249,115,22,${0.15 + intensity * 0.85})`;
              return <div key={h} title={`${DOW[d]} ${h}:00 — ${n} req`} className="w-4 h-4 m-[1px] rounded-sm" style={{ backgroundColor: bg }} />;
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

const WINDOW_LABELS = { min: 'Per minute', hour: 'Per hour', '6h': 'Per 6 hours', day: 'Per day' };

function KpiCard({ icon: Icon, label, value, valueClass = 'text-fg' }) {
  return (
    <div className="bg-panel border border-panel-border rounded-xl p-5 flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-orange/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-orange" />
      </div>
      <div>
        <p className="text-fg-muted text-xs mb-0.5">{label}</p>
        <p className={`text-xl font-bold ${valueClass}`}>{value}</p>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-surface rounded-xl border border-panel-border p-4">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className={`w-3.5 h-3.5 ${color}`} />}
        <p className="text-fg-muted text-xs">{label}</p>
      </div>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function UsageBar({ label, used, limit, unit = '' }) {
  const unlimited = !limit || limit <= 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-orange';
  const textColor = pct >= 90 ? 'text-red-400' : pct >= 70 ? 'text-yellow-400' : 'text-fg-muted';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-fg-muted">{label}</span>
        <span className={`font-medium ${textColor}`}>
          {used}{unit} / {unlimited ? '∞' : `${limit}${unit}`}
          {!unlimited && <span className="ml-1 opacity-60">({pct}%)</span>}
        </span>
      </div>
      <div className="w-full h-1.5 bg-field border border-field-border rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${unlimited ? 'bg-fg/10 w-full' : barColor}`}
          style={unlimited ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function MyUsage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isAdminView = !!userId;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);
  const [dateFilter, setDateFilter] = useState({ days: '7', startDate: null, endDate: null });
  const [model, setModel] = useState('');
  const [product, setProduct] = useState('');
  // Self-view splits into two tabs: 'overview' (the analytics) and 'tier' (tier,
  // allowed models, rate limits, budgets). Admin view has no analytics, so it
  // always renders the tier/budgets content and hides the tab switcher.
  const [tab, setTab] = useState('tier');
  // Which context BOTH tabs are scoped to: null = personal, else a team's
  // project_id — one shared selector, not one per tab. Enforcement is always
  // exactly one context at a time, so everything derived from it (Current
  // Tier, Allowed models, Totals, live limit bars, and Overview's analytics)
  // follows the same selection together, never a combined total that
  // wouldn't match anything the gateway actually enforces.
  const [teamId, setTeamId] = useState(null);
  // Tier & Budgets tab's data for the selected context (tier, totals, bars) —
  // fetched separately from `data` below so switching context doesn't trigger
  // load()'s full-page "Loading…" gate.
  const [contextData, setContextData] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);

  // Filter drawer (Overview tab) — the drawer itself is the shared FilterDrawer component.
  const [filterOpen, setFilterOpen] = useState(false);

  // Request Credits (self-view only) — asks the active ORG's admins for more of
  // this user's own credit limit. The org that owns the credit pool is the
  // active org, NOT the Personal/Team `teamId` above (that's a project scope).
  const { active } = useOrg();
  const orgId = active?.org_id;
  const [pendingReq, setPendingReq] = useState(null);   // an open request, or null
  const [reqOpen, setReqOpen] = useState(false);
  const [reqMessage, setReqMessage] = useState('');
  const [reqBusy, setReqBusy] = useState(false);
  const [reqErr, setReqErr] = useState(null);

  const loadPendingReq = useCallback(async () => {
    if (isAdminView || !orgId) { setPendingReq(null); return; }
    try {
      const { data } = await getMyCreditRequest(orgId);
      setPendingReq(data.pending || null);
    } catch { /* non-fatal — button just stays enabled */ }
  }, [isAdminView, orgId]);
  useEffect(() => { loadPendingReq(); }, [loadPendingReq]);

  const submitCreditRequest = async () => {
    setReqBusy(true); setReqErr(null);
    try {
      await createCreditRequest(orgId, { message: reqMessage.trim() });
      setReqOpen(false); setReqMessage('');
      await loadPendingReq();
    } catch (e) {
      setReqErr(e.response?.data?.detail || 'Could not send your request. Please try again.');
    } finally { setReqBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (isAdminView) {
        res = await getUserUsageAdmin(userId);
      } else {
        const custom = dateFilter.startDate && dateFilter.endDate;
        const d = custom ? null : (dateFilter.days === 'all' ? null : parseInt(dateFilter.days));
        res = await getMyUsage(
          d, custom ? dateFilter.startDate : null, custom ? dateFilter.endDate : null,
          model || null, product || null, teamId || null,
        );
      }
      setData(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [userId, isAdminView, dateFilter, model, product, teamId]);
  useEffect(() => { load(); }, [load]);

  // Tier & Budgets tab: tier, totals, and live limit bars for the selected
  // context — admin view has no context switcher, so it reuses `data` as-is.
  // `contextLoadedOnce` gates the page's initial "Loading…" only; switching
  // context afterward dims just the affected section (see contextLoading
  // below) instead of hiding the whole page again.
  const [contextLoadedOnce, setContextLoadedOnce] = useState(false);
  const loadContext = useCallback(async (tid) => {
    if (isAdminView) { setContextLoading(false); setContextLoadedOnce(true); return; }
    setContextLoading(true);
    try {
      const res = await getMyUsage(null, null, null, null, null, tid);
      setContextData(res.data);
    } catch { /* ignore */ } finally { setContextLoading(false); setContextLoadedOnce(true); }
  }, [isAdminView]);
  useEffect(() => { loadContext(teamId); }, [teamId, loadContext]);

  const axisColor = theme === 'light' ? '#6b7280' : '#9aa0aa';
  const tooltipStyle = {
    background: theme === 'light' ? '#ffffff' : '#141414',
    border: `1px solid ${theme === 'light' ? '#e5e7eb' : '#2a2a2a'}`,
    borderRadius: '8px', color: theme === 'light' ? '#111827' : '#f5f5f5',
  };

  const sendReport = async () => {
    setEmailing(true);
    setEmailMsg(null);
    try {
      const custom = dateFilter.startDate && dateFilter.endDate;
      const d = custom ? null : (dateFilter.days === 'all' ? null : parseInt(dateFilter.days));
      const res = await emailMyUsageReport(d, custom ? dateFilter.startDate : null, custom ? dateFilter.endDate : null);
      setEmailMsg({ ok: true, text: `Report sent to ${res.data.sent_to}` });
    } catch (e) {
      setEmailMsg({ ok: false, text: e.response?.data?.detail || 'Failed to send email' });
    } finally {
      setEmailing(false);
      setTimeout(() => setEmailMsg(null), 5000);
    }
  };

  if (loading || (!contextLoadedOnce && contextLoading)) return <div className="text-center text-fg-muted py-16">Loading…</div>;

  // Tier & Budgets tab: tier/totals/bars come from the context-scoped fetch;
  // admin view has no context switcher, so it reuses `data` (loadContext is a
  // no-op there — see loadContext above).
  const contextSource = isAdminView ? data : contextData;
  const tier = contextSource?.tier;
  const usage = data?.usage || { total_cost_usd: 0, total_calls: 0, by_model: [] };
  const contextTotals = contextSource?.context_totals || { total_cost_usd: 0, total_calls: 0 };
  const rlUsage = contextSource?.rate_limit_usage || [];
  const budgetUsage = contextSource?.budget_usage || [];
  const budgetUsagePerModel = contextSource?.budget_usage_per_model || [];
  // Depleting credit balance — distinct from the rolling-window budget above.
  // At most one of org/member is populated together (org context selected),
  // or personal alone (no org) — never all three at once.
  const credits = contextSource?.credits || {};
  const viewedUser = data?.user;
  const availableTeams = contextSource?.available_teams || [];

  // Analytics (self view only — the admin endpoint doesn't return these).
  const byProvider = data?.by_provider || [];
  const daily = data?.daily || [];
  const heatmap = data?.heatmap || { cells: [], max_requests: 0 };
  const latency = data?.latency || [];
  const dailyChart = daily.map(d => ({ date: formatDailyId(d._id), cost: d.cost, requests: d.requests }));
  const maxProvCost = Math.max(...byProvider.map(p => p.cost_usd), 0.000001);
  // Tab gating. Admin view: always tier/budgets, never overview.
  const showTier = isAdminView || tab === 'tier';
  const showOverview = !isAdminView && tab === 'overview';

  // Filter-dropdown options — models & products the user has used (from the backend).
  const availableModels = data?.filters?.models || [];
  const availableProducts = data?.filters?.products || [];
  const activeFilterCount =
    (model ? 1 : 0) + (product ? 1 : 0) +
    ((dateFilter.startDate && dateFilter.endDate) || dateFilter.days !== '7' ? 1 : 0);

  // Cost trend by provider — reshape [{date,provider,cost}] → [{date, ProviderA, ...}].
  const dbpRaw = data?.daily_by_provider || [];
  const dbpProviders = [...new Set(dbpRaw.map(r => r.provider))];
  const dbpMap = {};
  dbpRaw.forEach(r => { (dbpMap[r.date] = dbpMap[r.date] || { date: r.date })[r.provider] = r.cost; });
  const dailyProv = { rows: Object.values(dbpMap).sort((a, b) => a.date.localeCompare(b.date)), providers: dbpProviders };
  // by-model chart data (cost_usd/calls → cost/requests, as the modal uses).
  const byModel = (usage.by_model || []).map(m => ({ model: m.model, cost: m.cost_usd, requests: m.calls, avg_latency_ms: m.avg_latency_ms }));

  // Overall totals + token breakdown (self view).
  const ov = data?.overall || {};
  const ovReqs = ov.total_requests || 0;
  const avgCostReq = ovReqs ? (ov.total_cost || 0) / ovReqs : 0;
  const cacheLookups = (ov.total_cache_read_tokens || 0) + (ov.total_cache_write_tokens || 0);
  const cacheHit = cacheLookups > 0 ? (ov.total_cache_read_tokens / cacheLookups) * 100 : 0;
  const avgInputCost      = ovReqs ? (ov.total_input_cost || 0) / ovReqs : 0;
  const avgOutputCost     = ovReqs ? (ov.total_output_cost || 0) / ovReqs : 0;
  const avgCacheReadCost  = ovReqs ? (ov.total_cache_read_cost || 0) / ovReqs : 0;
  const avgCacheWriteCost = ovReqs ? (ov.total_cache_write_cost || 0) / ovReqs : 0;
  const tokenParts = [
    { k: 'Input',       v: ov.total_input_tokens || 0,       c: '#3b82f6' },
    { k: 'Output',      v: ov.total_output_tokens || 0,      c: '#a855f7' },
    { k: 'Cache read',  v: ov.total_cache_read_tokens || 0,  c: '#22c55e' },
    { k: 'Cache write', v: ov.total_cache_write_tokens || 0, c: '#eab308' },
    { k: 'Reasoning',   v: ov.total_reasoning_tokens || 0,   c: '#ec4899' },
  ];
  const tokenTotal = tokenParts.reduce((s, p) => s + p.v, 0) || 1;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          {isAdminView && (
            <button onClick={() => navigate('/users')} className="flex items-center gap-1.5 text-fg-muted hover:text-fg text-sm mb-2 transition-colors">
              <ArrowLeft className="w-4 h-4" />Back to Users
            </button>
          )}
          <h1 className="text-2xl font-bold text-fg">{isAdminView ? 'User Usage' : 'My Usage'}</h1>
          <p className="text-fg-muted text-sm mt-1">
            {isAdminView
              ? `Usage for ${viewedUser?.email || userId}`
              : 'Your tier, rate limits, allowed models, and spend.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isAdminView && (
            <button onClick={sendReport} disabled={emailing}
              className="flex items-center gap-2 px-3 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm disabled:opacity-50 transition-colors">
              <Mail className="w-4 h-4" />{emailing ? 'Sending…' : 'Email me the report'}
            </button>
          )}
          {/* context only renders on the Tier & Budgets tab — skip refetching it elsewhere. */}
          <button onClick={() => { load(); if (showTier) loadContext(teamId); }}
            className="flex items-center gap-2 px-3 py-2 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded-lg text-sm transition-colors">
            <RefreshCw className="w-4 h-4" />Refresh
          </button>
        </div>
      </div>

      {emailMsg && (
        <div className={`mb-5 p-3 rounded-lg text-sm flex justify-between items-center ${emailMsg.ok ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <span>{emailMsg.text}</span>
          <button onClick={() => setEmailMsg(null)} className="ml-4 opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Tab switcher (self view only) — Tier & Budgets vs Overview (analytics),
          with the context switcher at the right end of the same row. One shared
          selector for both tabs: Tier & Budgets' tier/totals/bars and Overview's
          analytics all scope to whichever context is picked here. */}
      {!isAdminView && (
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-1 p-1 bg-panel border border-panel-border rounded-lg w-fit">
            {[
              { key: 'tier', label: 'Tier & Budgets', icon: ShieldCheck },
              { key: 'overview', label: 'Overview', icon: BarChart3 },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  tab === key ? 'bg-orange text-white' : 'text-fg-muted hover:text-fg'}`}
              >
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {orgId && (
              pendingReq ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-fg-muted bg-field border border-field-border rounded-lg" title="You already have a request awaiting review">
                  <Coins className="w-4 h-4" /> Request pending
                </span>
              ) : (
                <button
                  onClick={() => { setReqErr(null); setReqMessage(''); setReqOpen(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange hover:bg-orange-hover text-white rounded-lg"
                >
                  <Coins className="w-4 h-4" /> Request Credits
                </button>
              )
            )}
            {availableTeams.length > 0 && (
              <Select
                className="w-48"
                align="right"
                value={teamId || ''}
                onChange={(v) => setTeamId(v || null)}
                options={[
                  { value: '', label: 'Personal' },
                  ...availableTeams.map((tm) => ({ value: tm.project_id, label: `Team: ${tm.name || tm.project_id}` })),
                ]}
              />
            )}
          </div>
        </div>
      )}

      {/* Request Credits modal — asks the active org's admins to raise this
          user's own credit limit. */}
      {reqOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !reqBusy && setReqOpen(false)}>
          <div className="w-full max-w-md bg-panel border border-panel-border rounded-2xl p-6 shadow-2xl shadow-black/40" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="req-credits-title">
            <div className="flex items-center justify-between mb-1">
              <h2 id="req-credits-title" className="font-display text-lg font-bold text-fg flex items-center gap-2">
                <Coins className="w-5 h-5 text-orange" /> Request Credits
              </h2>
              <button onClick={() => !reqBusy && setReqOpen(false)} className="text-fg-muted hover:text-fg"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-fg-muted mb-4">
              Request <span className="text-fg">{active?.name || 'your organization'}</span> for more credits.
            </p>
            {reqErr && (
              <div className="mb-4 p-3 rounded-lg border text-sm bg-red-500/10 border-red-500/25 text-red-400">{reqErr}</div>
            )}
            <label className="block text-xs text-fg-muted mb-1">Note <span className="opacity-60">(optional)</span></label>
            <textarea
              value={reqMessage}
              onChange={(e) => setReqMessage(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Why do you need more credit?"
              className="w-full mb-5 px-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm placeholder-fg-muted/60 focus:border-orange resize-y"
            />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setReqOpen(false)} disabled={reqBusy} className="px-4 py-2 text-fg-muted hover:text-fg text-sm">Cancel</button>
              <button onClick={submitCreditRequest} disabled={reqBusy} className="px-4 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm disabled:opacity-50">
                {reqBusy ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters button (Overview tab) — opens the drawer that drives the analytics */}
      {showOverview && (
        <div className="flex items-center justify-end mb-5">
          <button
            onClick={() => setFilterOpen(true)}
            className="relative flex items-center gap-2 px-3 py-1.5 bg-field border border-field-border hover:border-orange/50 text-fg rounded-lg text-xs font-medium transition-colors"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 bg-orange text-white text-xs font-semibold rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      )}

      {/* ── Tier & Budgets tab: KPI row, allowed models, rate limits, budgets ── */}
      {showTier && (<>
      {/* KPI row — Users total usage in selected org */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        <KpiCard icon={Layers} label="Current Tier" value={tier?.name || '—'} />
        <KpiCard icon={DollarSign} label="Total Spent" value={`$${(isAdminView ? usage.total_cost_usd : contextTotals.total_cost_usd)?.toFixed(6) ?? '0.000000'}`} valueClass="text-green-400" />
        <KpiCard icon={Activity} label="Total Calls" value={((isAdminView ? usage.total_calls : contextTotals.total_calls) || 0).toLocaleString()} />
      </div>

      {/* Allowed models — full width */}
      <div className="bg-panel border border-panel-border rounded-xl p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-orange" />
          <h2 className="text-fg font-semibold text-sm">Allowed models</h2>
        </div>
        {tier?.models?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {tier.models.map((m) => (
              <span key={m} className="px-2 py-0.5 bg-orange/10 text-orange text-xs rounded font-mono">{m}</span>
            ))}
          </div>
        ) : (
          <p className="text-fg-muted text-sm">{tier ? 'Unrestricted — all configured models are available to you.' : 'No tier assigned.'}</p>
        )}
      </div>

      {/* Rate limits, Budget usage, Budget usage per model — dimmed in place while
          switching context, instead of the whole-page loading gate load() uses. */}
      <div className={contextLoading ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'}>
      {/* Rate limits — full width */}
      <div className="bg-panel border border-panel-border rounded-xl p-5 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-4 h-4 text-orange" />
          <h2 className="text-fg font-semibold text-sm">Rate limit usage (per model)</h2>
        </div>
        {rlUsage.length === 0 ? (
          <p className="text-fg-muted text-sm">No models / rate limits configured.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {rlUsage.map((entry) => (
              <div key={entry.model}>
                <p className="text-xs font-mono font-medium text-fg mb-2.5">{entry.model}</p>
                <div className="space-y-2.5">
                  {(entry.windows || []).map((w) => (
                    <UsageBar
                      key={w.window}
                      label={WINDOW_LABELS[w.window] || w.window}
                      used={w.used}
                      limit={w.limit}
                      unit=" req"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Budget usage */}
      <div className="bg-panel border border-panel-border rounded-xl p-5 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-4 h-4 text-orange" />
          <h2 className="text-fg font-semibold text-sm">Budget usage (USD)</h2>
        </div>
        {budgetUsage.length === 0 ? (
          <p className="text-fg-muted text-sm">No spend budget is set on your tier — usage is unlimited (cost-wise). An admin can add daily/hourly $ caps to your tier.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {budgetUsage.map((w) => (
              <UsageBar
                key={w.window}
                label={WINDOW_LABELS[w.window] || w.window}
                used={w.spent.toFixed(4)}
                limit={w.limit ? w.limit.toFixed(2) : 0}
              />
            ))}
          </div>
        )}
      </div>

      {/* Budget usage per model */}
      <div className="bg-panel border border-panel-border rounded-xl p-5 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-4 h-4 text-orange" />
          <h2 className="text-fg font-semibold text-sm">Budget usage (per model)</h2>
        </div>
        {budgetUsagePerModel.length === 0 ? (
          <p className="text-fg-muted text-sm">No per-model spend cap is set on your tier — model spend is only limited by the tier-wide budget above.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {budgetUsagePerModel.map((entry) => (
              <div key={entry.model}>
                <p className="text-xs font-mono font-medium text-fg mb-2.5">{entry.model}</p>
                <div className="space-y-2.5">
                  {(entry.windows || []).map((w) => (
                    <UsageBar
                      key={w.window}
                      label={WINDOW_LABELS[w.window] || w.window}
                      used={w.spent.toFixed(4)}
                      limit={w.limit ? w.limit.toFixed(2) : 0}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Credits — depleting balance, read-only here (an admin sets it).
          The org-wide pool is organization-level info, not "my usage" — it's
          shown (and managed) in exactly one place, the Credits card on
          /users, so owners/admins aren't seeing the same number scattered
          across every page. Only this user's sub-cap WITHIN this org — set
          per-org on org_members, never a global field — belongs here. */}
      {credits.member && (
        <div className="bg-panel border border-panel-border rounded-xl p-5 mb-5">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-4 h-4 text-orange" />
            <h2 className="text-fg font-semibold text-sm">Credits</h2>
          </div>
          <UsageBar label="Your credit limit" used={(credits.member.credit_limit - credits.member.credits_remaining).toFixed(2)} limit={credits.member.credit_limit.toFixed(2)} unit=" USD" />
        </div>
      )}
      </div>
      </>)}

      {/* ── Overview tab: analytics — same order as Users → View usage ── */}
      {showOverview && (
        <>
          {/* 1. Overall stat cards (6 totals + 4 per-type cost, as in the modal) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            <StatCard label="Total Cost"    value={`$${(ov.total_cost || 0).toFixed(2)}`}   color="text-green-400"  icon={TrendingUp} />
            <StatCard label="Requests"      value={fmt(ov.total_requests)}                  color="text-orange"     icon={Activity}   />
            <StatCard label="Input Tokens"  value={fmtM(ov.total_input_tokens)}             color="text-blue-400"   icon={Layers}     />
            <StatCard label="Output Tokens" value={fmtM(ov.total_output_tokens)}            color="text-purple-400" icon={Zap}        />
            <StatCard label="Avg Cost/Req"  value={`$${avgCostReq.toFixed(4)}`}             color="text-yellow-400" icon={TrendingUp} />
            <StatCard label="Cache Hit"     value={`${cacheHit.toFixed(1)}%`}               color="text-teal-400"   icon={Clock}      />
            <StatCard label="Avg Input Cost/Req"       value={`$${avgInputCost.toFixed(4)}`}      color="text-blue-400"   icon={Layers}     />
            <StatCard label="Avg Output Cost/Req"      value={`$${avgOutputCost.toFixed(4)}`}     color="text-purple-400" icon={Zap}        />
            <StatCard label="Avg Cache-Read Cost/Req"  value={`$${avgCacheReadCost.toFixed(4)}`}  color="text-green-400"  icon={Clock}      />
            <StatCard label="Avg Cache-Write Cost/Req" value={`$${avgCacheWriteCost.toFixed(4)}`} color="text-yellow-400" icon={TrendingUp} />
          </div>

          {/* 2. Token breakdown (cost drivers) */}
          <div className="bg-panel border border-panel-border rounded-xl p-5 mb-5">
            <h3 className="text-sm font-semibold text-fg mb-4">Token breakdown (cost drivers)</h3>
            <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-fg/5">
              {tokenParts.map(p => p.v > 0 && (
                <div key={p.k} style={{ width: `${(p.v / tokenTotal) * 100}%`, backgroundColor: p.c }} title={`${p.k}: ${fmt(p.v)}`} />
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {tokenParts.map(p => (
                <div key={p.k}>
                  <div className="flex items-center gap-1.5 text-xs text-fg-muted">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.c }} />
                    {p.k}
                  </div>
                  <div className="text-fg text-sm font-semibold mt-0.5">{fmtM(p.v)}</div>
                  <div className="text-[11px] text-fg-muted">{((p.v / tokenTotal) * 100).toFixed(1)}%</div>
                </div>
              ))}
            </div>
          </div>

          {/* 3. Requests heatmap */}
          <div className="bg-panel border border-panel-border rounded-xl p-5 mb-5">
            <h3 className="text-sm font-semibold text-fg mb-4">Requests by hour (IST)</h3>
            <MiniHeatmap cells={heatmap.cells} maxRequests={heatmap.max_requests} />
          </div>
        </>
      )}

      {/* 4. Usage by model table — Overview tab (self), and always in admin view */}
      {(showOverview || isAdminView) && (
      <div className="bg-panel border border-panel-border rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-panel-border">
          <BarChart3 className="w-4 h-4 text-orange" />
          <h2 className="text-fg font-semibold text-sm">Usage by model</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-fg-muted bg-field/30 border-b border-panel-border">
                <th className="px-5 py-3 font-medium">Model</th>
                <th className="px-5 py-3 font-medium text-right">Calls</th>
                <th className="px-5 py-3 font-medium text-right">Input tokens</th>
                <th className="px-5 py-3 font-medium text-right">Output tokens</th>
                <th className="px-5 py-3 font-medium text-right">Cache read tok</th>
                <th className="px-5 py-3 font-medium text-right">Cache write tok</th>
                <th className="px-5 py-3 font-medium text-right">Cost (USD)</th>
              </tr>
            </thead>
            <tbody>
              {usage.by_model.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-fg-muted">No usage yet.</td></tr>
              ) : usage.by_model.map((m) => (
                <tr key={m.model} className="border-b border-panel-border/50 last:border-0 hover:bg-field/20 transition-colors">
                  <td className="px-5 py-3 font-mono text-fg text-xs">{m.model}</td>
                  <td className="px-5 py-3 text-right text-fg"><TokenValue value={m.calls} unit="calls" /></td>
                  <td className="px-5 py-3 text-right text-fg-muted"><TokenValue value={m.input_tokens} /></td>
                  <td className="px-5 py-3 text-right text-fg-muted"><TokenValue value={m.output_tokens} /></td>
                  <td className="px-5 py-3 text-right text-fg-muted"><TokenValue value={m.cache_read_tokens} /></td>
                  <td className="px-5 py-3 text-right text-fg-muted"><TokenValue value={m.cache_write_tokens} /></td>
                  <td className="px-5 py-3 text-right text-green-400 font-medium"><TokenValue value={m.cost_usd} currency /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* ── Overview tab (cont.): analytics charts — Daily → Provider → Latency ── */}
      {showOverview && (
        <>
          {/* 5. Daily cost + requests */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
            <div className="bg-panel border border-panel-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-fg mb-4">Daily cost</h3>
              {dailyChart.length === 0 ? <p className="text-fg-muted text-sm">No data.</p> : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={dailyChart} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: axisColor }} interval={tickInterval(dailyChart.length)} />
                    <YAxis tick={{ fontSize: 10, fill: axisColor }} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => `$${(v || 0).toFixed(4)}`} />
                    <Line type="monotone" dataKey="cost" stroke="#22c55e" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="bg-panel border border-panel-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-fg mb-4">Daily requests</h3>
              {dailyChart.length === 0 ? <p className="text-fg-muted text-sm">No data.</p> : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={dailyChart} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: axisColor }} interval={tickInterval(dailyChart.length)} />
                    <YAxis tick={{ fontSize: 10, fill: axisColor }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="requests" fill="#f97316" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* 6. Cost trend by provider */}
          {dailyProv.rows.length > 0 && (
            <div className="bg-panel border border-panel-border rounded-xl p-5 mt-5">
              <h3 className="text-sm font-semibold text-fg mb-4">Cost trend by provider</h3>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyProv.rows} margin={{ top: 5, right: 18, left: 0, bottom: 5 }}>
                    <XAxis dataKey="date" stroke={axisColor} fontSize={11} tickFormatter={formatDailyId} interval={tickInterval(dailyProv.rows.length, 12)} />
                    <YAxis stroke={axisColor} fontSize={11} tickFormatter={v => `$${v}`} />
                    <Tooltip contentStyle={tooltipStyle} labelFormatter={formatDailyId} formatter={v => [`$${Number(v).toFixed(4)}`]} />
                    <Legend wrapperStyle={{ fontSize: '11px', color: axisColor }} />
                    {dailyProv.providers.map((p, i) => (
                      <Line key={p} type="monotone" dataKey={p}
                        stroke={PROVIDER_COLORS[i % PROVIDER_COLORS.length]}
                        strokeWidth={2} dot={false} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* 7. Cost by Model + Requests by Model */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
            <div className="bg-panel border border-panel-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-fg mb-4">Cost by model</h3>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byModel.slice(0, 8)} layout="vertical">
                    <XAxis type="number" stroke={axisColor} fontSize={11} tickFormatter={v => `$${v}`} />
                    <YAxis type="category" dataKey="model" stroke={axisColor} fontSize={9} width={110} />
                    <Tooltip contentStyle={tooltipStyle} formatter={v => [`$${v.toFixed(4)}`, 'Cost']} />
                    <Bar dataKey="cost" fill="#f97316" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-panel border border-panel-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-fg mb-4">Requests by model</h3>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byModel.slice(0, 8)} layout="vertical">
                    <XAxis type="number" stroke={axisColor} fontSize={11} />
                    <YAxis type="category" dataKey="model" stroke={axisColor} fontSize={9} width={110} />
                    <Tooltip contentStyle={tooltipStyle} formatter={v => [fmt(v), 'Requests']} />
                    <Bar dataKey="requests" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* 8. Avg Latency by Model + Latency Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
            <div className="bg-panel border border-panel-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-fg mb-4">Avg latency by model</h3>
              <div className="space-y-3 max-h-52 overflow-y-auto">
                {byModel.slice(0, 10).map((item, i) => {
                  const lat = item.avg_latency_ms || 0;
                  const maxLat = Math.max(...byModel.map(m => m.avg_latency_ms || 0), 1);
                  return (
                    <div key={i}>
                      <div className="flex justify-between mb-0.5">
                        <span className="text-fg text-xs truncate max-w-[130px]" title={item.model}>{item.model}</span>
                        <span className={`text-xs font-medium ${lat > 5000 ? 'text-red-400' : lat > 2000 ? 'text-yellow-400' : 'text-green-400'}`}>
                          {lat > 1000 ? `${(lat / 1000).toFixed(1)}s` : `${Math.round(lat)}ms`}
                        </span>
                      </div>
                      <div className="h-1.5 bg-surface rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${lat > 5000 ? 'bg-red-500' : lat > 2000 ? 'bg-yellow-500' : 'bg-green-500'}`}
                          style={{ width: `${maxLat > 0 ? (lat / maxLat) * 100 : 0}%` }} />
                      </div>
                      <p className="text-fg-muted text-[10px] mt-0.5">{fmt(item.requests)} requests</p>
                    </div>
                  );
                })}
                {byModel.length === 0 && <p className="text-fg-muted text-xs">No latency data.</p>}
              </div>
            </div>

            <div className="bg-panel border border-panel-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-fg mb-4">Latency distribution</h3>
              {latency.length === 0 ? <p className="text-fg-muted text-sm">No data.</p> : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={latency}>
                    <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: axisColor }} />
                    <YAxis tick={{ fontSize: 10, fill: axisColor }} />
                    <Tooltip contentStyle={tooltipStyle} formatter={v => [fmt(v), 'Requests']} />
                    <Bar dataKey="count" fill="#a855f7" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* 7. Cost by provider (last, as in the modal) */}
          <div className="bg-panel border border-panel-border rounded-xl p-5 mt-5">
            <h3 className="text-sm font-semibold text-fg mb-4">Cost by provider</h3>
            {byProvider.length === 0 ? <p className="text-fg-muted text-sm">No data.</p> : (
              <div className="space-y-2.5">
                {byProvider.map((p, i) => (
                  <div key={p.provider}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-fg font-mono">{p.provider || 'unknown'}</span>
                      <span className="text-green-400">${(p.cost_usd || 0).toFixed(4)} · {p.calls} calls</span>
                    </div>
                    <div className="w-full h-1.5 bg-field border border-field-border rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(p.cost_usd / maxProvCost) * 100}%`, backgroundColor: PROVIDER_COLORS[i % PROVIDER_COLORS.length] }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Filter drawer (Overview) — shared component; drives the analytics fetch.
          Context isn't a filter here — it's the same selector as the Tier &
          Budgets tab, up in the tab row, shared by both tabs. */}
      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        theme={theme}
        value={{ dateFilter, model, product }}
        selects={[
          { key: 'model', label: 'Model', allLabel: 'All models', options: availableModels },
          { key: 'product', label: 'Product', allLabel: 'All products', options: availableProducts },
        ]}
        onApply={(next) => { setDateFilter(next.dateFilter); setModel(next.model); setProduct(next.product); }}
      />
    </div>
  );
}
